import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, makeStyles, mergeClasses, ProgressBar, shorthands, Spinner, Text, tokens } from '@fluentui/react-components';
import { ArrowLeft20Regular, ArrowRight20Regular, Checkmark16Regular, Checkmark20Regular, Dismiss20Regular } from '@fluentui/react-icons';
import { PollQuestionType } from '../services/DataService';
import { useDataService } from '../app/DataServiceContext';
import { useAppToast } from '../app/ToastContext';
import { useIsMobile } from '../app/useMediaQuery';
import { createEmptyQuestion, isChoiceType, validateQuestion, applyQuestionType } from '../lib/pollQuestions';
import { getEventTypeLabel, getQuestionTypeLabel, questionsCount } from '../lib/pollLabels';
import { formatPeriod, yesNo } from '../lib/format';
import {
    createEmptyPollForm,
    firstError,
    hasErrors,
    IPollFormErrors,
    IPollFormValues,
    toCreateRequest,
    validateDates,
    validateDetails,
    validateQuestions,
} from '../components/forms/pollForm';
import { useGeo } from '../components/forms/useGeo';
import PollDetailsFields from '../components/forms/PollDetailsFields';
import PollSettingsFields from '../components/forms/PollSettingsFields';
import QuestionEditor from '../components/forms/QuestionEditor';
import QuestionsEditor from '../components/forms/QuestionsEditor';
import QuestionTypePicker from '../components/forms/QuestionTypePicker';
import PageHeader from '../components/ui/PageHeader';
import Section from '../components/ui/Section';
import ErrorBar from '../components/ui/ErrorBar';
import FormFooter from '../components/ui/FormFooter';
import DescriptionList from '../components/ui/DescriptionList';

type StepId = 'type' | 'content' | 'settings' | 'review';

interface IStep {
    id: StepId;
    title: string;
    hint: string;
}

const QUICK_STEPS: IStep[] = [
    { id: 'type', title: 'Тип вопроса', hint: 'Как участники будут отвечать' },
    { id: 'content', title: 'Содержание', hint: 'Название, мероприятие и вопрос' },
    { id: 'settings', title: 'Настройки', hint: 'Правила и сроки' },
    { id: 'review', title: 'Проверка', hint: 'Всё ли верно' },
];

const COMPLEX_STEPS: IStep[] = [
    { id: 'content', title: 'Содержание', hint: 'Название, мероприятие и вопросы' },
    { id: 'settings', title: 'Настройки', hint: 'Правила и сроки' },
    { id: 'review', title: 'Проверка', hint: 'Всё ли верно' },
];

const useStyles = makeStyles({
    stepper: {
        display: 'grid',
        gridAutoFlow: 'column',
        gridAutoColumns: 'minmax(0, 1fr)',
        gap: '8px',
    },
    step: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        borderRadius: tokens.borderRadiusLarge,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground3,
        textAlign: 'left',
        cursor: 'default',
        minWidth: 0,
    },
    stepClickable: {
        cursor: 'pointer',
        ':hover': {
            ...shorthands.borderColor(tokens.colorBrandStroke1),
        },
    },
    stepActive: {
        ...shorthands.borderColor(tokens.colorBrandStroke1),
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground1,
    },
    stepDone: {
        color: tokens.colorNeutralForeground1,
    },
    stepIndex: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '26px',
        height: '26px',
        borderRadius: tokens.borderRadiusCircular,
        border: `1px solid currentColor`,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightSemibold,
        flexShrink: 0,
    },
    stepIndexActive: {
        backgroundColor: tokens.colorBrandBackground,
        ...shorthands.borderColor(tokens.colorBrandBackground),
        color: tokens.colorNeutralForegroundOnBrand,
    },
    stepIndexDone: {
        backgroundColor: tokens.colorBrandBackground2,
        ...shorthands.borderColor(tokens.colorBrandBackground2),
        color: tokens.colorBrandForeground1,
    },
    stepText: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },
    stepTitle: {
        fontWeight: tokens.fontWeightSemibold,
        fontSize: tokens.fontSizeBase300,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    stepHint: {
        fontSize: tokens.fontSizeBase200,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    mobileStepper: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    mobileStepLine: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: '8px',
    },
    stack: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        '@media (max-width: 767px)': {
            gap: '16px',
        },
    },
    reviewGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '20px',
        alignItems: 'start',
        '@media (max-width: 1023px)': {
            gridTemplateColumns: 'minmax(0, 1fr)',
        },
    },
    reviewQuestion: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        paddingTop: '10px',
        paddingBottom: '10px',
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        ':first-child': {
            borderTop: 'none',
            paddingTop: 0,
        },
    },
    hint: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
    },
});

const PollWizardPage: React.FC = () => {
    const styles = useStyles();
    const navigate = useNavigate();
    const isMobile = useIsMobile();
    const toast = useAppToast();
    const dataService = useDataService();
    const { mode } = useParams<{ mode: string }>();
    const quick = mode !== 'complex';
    const steps = quick ? QUICK_STEPS : COMPLEX_STEPS;

    const [stepIndex, setStepIndex] = React.useState(0);
    const [values, setValues] = React.useState<IPollFormValues>(() => ({
        ...createEmptyPollForm(),
        questions: quick ? [createEmptyQuestion(PollQuestionType.Rating)] : [],
    }));
    const [showErrors, setShowErrors] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string>();
    const geo = useGeo(values.geoRegionId);

    const patch = (p: Partial<IPollFormValues>): void => setValues((prev) => ({ ...prev, ...p }));
    const step = steps[stepIndex];
    const quickQuestion = values.questions[0];

    // Validation is per step so an early step never nags about a later one.
    const stepErrors = React.useMemo<IPollFormErrors>(() => {
        switch (step.id) {
            case 'content': {
                const details = validateDetails(values);
                if (quick) {
                    const error = quickQuestion ? validateQuestion(quickQuestion) : 'Добавьте вопрос.';
                    return { ...details, questionErrors: error && quickQuestion ? { [quickQuestion.key]: error } : {} };
                }
                return { ...details, ...validateQuestions(values.questions) };
            }
            case 'settings':
                return { dates: validateDates(values), questionErrors: {} };
            default:
                return { questionErrors: {} };
        }
    }, [step.id, values, quick, quickQuestion]);

    const stepValid = !hasErrors(stepErrors);

    const goNext = (): void => {
        if (!stepValid) {
            setShowErrors(true);
            return;
        }
        setShowErrors(false);
        setStepIndex((i) => Math.min(steps.length - 1, i + 1));
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const goBack = (): void => {
        setShowErrors(false);
        setStepIndex((i) => Math.max(0, i - 1));
    };

    const save = async (): Promise<void> => {
        const all = { ...validateDetails(values), dates: validateDates(values), ...validateQuestions(values.questions) };
        if (hasErrors(all)) {
            setSaveError(firstError(all));
            return;
        }
        try {
            setSaving(true);
            setSaveError(undefined);
            const created = await dataService.createPoll(toCreateRequest(values, quick));
            toast.success('Опрос создан', created?.Title ?? values.title);
            navigate(created?.Id ? `/polls/${encodeURIComponent(created.Id)}` : '/polls', { replace: true });
        } catch (e) {
            setSaveError((e as Error)?.message ?? 'Не удалось создать опрос. Попробуйте снова.');
        } finally {
            setSaving(false);
        }
    };

    const renderStepper = (): React.ReactNode => {
        if (isMobile) {
            return (
                <div className={styles.mobileStepper}>
                    <div className={styles.mobileStepLine}>
                        <Text weight="semibold">{step.title}</Text>
                        <Text className={styles.hint}>Шаг {stepIndex + 1} из {steps.length}</Text>
                    </div>
                    <ProgressBar value={(stepIndex + 1) / steps.length} thickness="large" color="brand" aria-label={`Шаг ${stepIndex + 1} из ${steps.length}`} />
                </div>
            );
        }
        return (
            <ol className={styles.stepper} style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                {steps.map((s, index) => {
                    const done = index < stepIndex;
                    const active = index === stepIndex;
                    return (
                        <li
                            key={s.id}
                            className={mergeClasses(styles.step, done && styles.stepDone, done && styles.stepClickable, active && styles.stepActive)}
                            aria-current={active ? 'step' : undefined}
                            onClick={done ? () => { setShowErrors(false); setStepIndex(index); } : undefined}
                        >
                            <span className={mergeClasses(styles.stepIndex, active && styles.stepIndexActive, done && styles.stepIndexDone)}>
                                {done ? <Checkmark16Regular /> : index + 1}
                            </span>
                            <span className={styles.stepText}>
                                <span className={styles.stepTitle}>{s.title}</span>
                                <span className={styles.stepHint}>{s.hint}</span>
                            </span>
                        </li>
                    );
                })}
            </ol>
        );
    };

    const renderStep = (): React.ReactNode => {
        switch (step.id) {
            case 'type':
                return (
                    <Section title="Как участники будут отвечать?" description="Быстрый опрос состоит из одного вопроса. Тип можно будет уточнить позже через шаблоны.">
                        <QuestionTypePicker
                            value={quickQuestion?.Type ?? PollQuestionType.Rating}
                            onChange={(type) => patch({ questions: [applyQuestionType(quickQuestion ?? createEmptyQuestion(type), type)] })}
                        />
                    </Section>
                );
            case 'content':
                return (
                    <div className={styles.stack}>
                        <Section title="Об опросе">
                            <PollDetailsFields values={values} onChange={patch} geo={geo} errors={showErrors ? stepErrors : undefined} autoFocus />
                        </Section>
                        {quick ? (
                            <Section title="Вопрос" description={`Тип: ${getQuestionTypeLabel(quickQuestion?.Type)}`}>
                                {quickQuestion && (
                                    <QuestionEditor
                                        question={quickQuestion}
                                        onChange={(q) => patch({ questions: [q] })}
                                        hideType
                                        hideRequired
                                        showErrors={showErrors}
                                        error={showErrors ? stepErrors.questionErrors[quickQuestion.key] : undefined}
                                    />
                                )}
                            </Section>
                        ) : (
                            <Section title="Вопросы" description={values.questions.length ? questionsCount(values.questions.length) : 'Добавьте хотя бы один вопрос'}>
                                <QuestionsEditor
                                    questions={values.questions}
                                    onChange={(questions) => patch({ questions })}
                                    errors={stepErrors.questionErrors}
                                    showErrors={showErrors}
                                    listError={stepErrors.questions}
                                />
                            </Section>
                        )}
                    </div>
                );
            case 'settings':
                return (
                    <Section title="Правила и сроки">
                        <PollSettingsFields values={values} onChange={patch} datesError={showErrors ? stepErrors.dates : undefined} />
                    </Section>
                );
            case 'review':
            default:
                return (
                    <div className={styles.reviewGrid}>
                        <Section title="Об опросе">
                            <DescriptionList items={[
                                { term: 'Название', details: values.title || '—' },
                                { term: 'Описание', details: values.description || '—' },
                                { term: 'Режим', details: quick ? 'Быстрый' : 'Комплексный' },
                                { term: 'Мероприятие', details: getEventTypeLabel(values.eventTypeCode) },
                                { term: 'Регион', details: geo.regions.find((r) => r.Id === values.geoRegionId)?.Name ?? '—' },
                                ...(values.geoTerritoryId ? [{ term: 'Территория', details: geo.territories.find((t) => t.Id === values.geoTerritoryId)?.Name ?? '—' }] : []),
                                { term: 'Один ответ', details: yesNo(values.singleResponse) },
                                { term: 'Показ результатов', details: yesNo(values.completedDisplayResults) },
                                { term: 'Сообщение в конце', details: values.completedMessage || '—' },
                                { term: 'Период', details: formatPeriod(values.opensAt?.toISOString(), values.closesAt?.toISOString()) },
                            ]} />
                        </Section>
                        <Section title="Вопросы" description={questionsCount(values.questions.length)}>
                            {values.questions.map((q, index) => (
                                <div key={q.key} className={styles.reviewQuestion}>
                                    <Text weight="semibold">{index + 1}. {q.Title}</Text>
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                                        <Badge appearance="tint" color="brand" size="small" shape="rounded">{getQuestionTypeLabel(q.Type)}</Badge>
                                        {q.Required && <Badge appearance="outline" color="subtle" size="small" shape="rounded">Обязательный</Badge>}
                                    </div>
                                    {isChoiceType(q.Type) && (
                                        <Text className={styles.hint}>{q.Options.map((o) => o.Text).filter(Boolean).join(' · ')}</Text>
                                    )}
                                </div>
                            ))}
                        </Section>
                    </div>
                );
        }
    };

    const isLast = stepIndex === steps.length - 1;

    return (
        <>
            <PageHeader
                backTo={{ to: '/polls', label: 'Все опросы' }}
                title={quick ? 'Новый быстрый опрос' : 'Новый комплексный опрос'}
                subtitle={quick ? 'Один вопрос, минимум настроек: готов за пару минут.' : 'Несколько вопросов разных типов в одном опросе.'}
            />

            {renderStepper()}

            {showErrors && !stepValid && (
                <ErrorBar message={firstError(stepErrors) ?? 'Проверьте заполнение шага.'} title="Заполните обязательные поля" onDismiss={() => setShowErrors(false)} />
            )}
            <ErrorBar message={saveError} title="Не удалось создать опрос" onDismiss={() => setSaveError(undefined)} />

            {renderStep()}

            <FormFooter
                start={(
                    <Button appearance="subtle" icon={<Dismiss20Regular />} disabled={saving} onClick={() => navigate('/polls')}>
                        Отмена
                    </Button>
                )}
                end={(
                    <>
                        <Button appearance="secondary" icon={<ArrowLeft20Regular />} disabled={stepIndex === 0 || saving} onClick={goBack}>
                            Назад
                        </Button>
                        {isLast ? (
                            <Button appearance="primary" icon={saving ? <Spinner size="tiny" /> : <Checkmark20Regular />} disabled={saving} onClick={() => { void save(); }}>
                                {saving ? 'Создаём' : 'Создать опрос'}
                            </Button>
                        ) : (
                            <Button appearance="primary" icon={<ArrowRight20Regular />} iconPosition="after" onClick={goNext}>
                                Далее
                            </Button>
                        )}
                    </>
                )}
            />
        </>
    );
};

export default PollWizardPage;
